import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import SparkMD5 from 'spark-md5';

import { RootState } from '$store';
import Select from '~components/generic/elements/Select';
import Loader from '~components/generic/loader/Loader';
import useAppDispatch from '~hooks/useAppDispatch';
import useMediaLibrarySettings from '~modules/media-library/context/useMediaLibrarySettings';
import {
  fetchRelationList,
  removeRelationOfAsset,
  setSelectedDocumentType,
  upload,
} from '~modules/media-library/controller/thunks/assetRelationsSlice';
import { removeRelevantDocument } from '~modules/media-library/controller/thunks/contribute';
import { AssetId } from '~modules/media-library/model/Asset';
import UploadWrapper from '~modules/media-library/view/Contribute/RelevantDocuments/UploadWrapper/UploadWrapper';
import ItemUpload from '~modules/media-library/view/Contribute/UploadAssetsForm/UploadItem';
import Item from '../UploadAssetsForm/Item/Item';
import styles from './RelevantDocuments.module.css';

export interface UploadType {
  id: string;
  label: string;
  assetType: string;
  allowedFormats: string[];
  relationType: string;
}

export interface UploadTypes {
  [key: string]: UploadType;
}

interface RelevantDocumentsProps {
  assetId: AssetId;
  additionalUploadTypes: UploadTypes;
  selectedRelevantDocuments: any;
}

const RelevantDocuments: React.FC<RelevantDocumentsProps> = ({ assetId, selectedRelevantDocuments }) => {
  const data = useSelector((store: RootState) => store.assetRelations);
  const contribute = useSelector((store: RootState) => store.contribute);
  const dispatch = useAppDispatch();
  const { allFiles, selectedDocumentType, relevantDocuments, relevantDocumentsLoading, uploadInProgress } = data;
  const { additionalSettings } = useMediaLibrarySettings();

  const useFirst50kBytes = additionalSettings.upload.file_checksums_50k;

  const additionalUploadTypes: UploadTypes =
    additionalSettings.contribution_types[contribute.fileSelected.contributionType]?.additional_upload_types;

  const allowedFormats = additionalUploadTypes[selectedDocumentType]?.allowedFormats;

  const dropdownLabels = Object.fromEntries(
    Object.entries(additionalUploadTypes).map(([key, value]) => [key, value.label]),
  );

  const items = Object.entries(dropdownLabels).map(([value, label]) => ({
    value,
    label,
  }));

  useEffect(() => {
    if (items.length > 0) {
      dispatch(setSelectedDocumentType(items[0].value));
    }
  }, [items, dispatch]);

  // Filter out any files that already exist in relevantDocuments by comparing relatedAssetId
  // Only keeps files from selectedRelevantDocuments that don't have a matching relatedAssetId in relevantDocuments
  // This ensures we only show unique files that aren't duplicated between the two arrays
  const uniqueSelectedFiles = selectedRelevantDocuments?.filter(
    selectedFile => !relevantDocuments?.some(relatedFile => relatedFile.relatedAssetId === selectedFile.relatedAssetId),
  );

  useEffect(() => {
    dispatch(fetchRelationList(assetId));
  }, [dispatch, assetId]);

  const handleDelete = (file: any) => {
    dispatch(removeRelationOfAsset(file));
  };

  const handleDeleteForSelectedFile = file => {
    dispatch(removeRelevantDocument(file.relatedAssetId));
  };

  const handleUpload = (files: File[]) => {
    const uniqueFilesWithHashes: { file: File; hash: string }[] = [];
    const checksums: Set<string> = new Set();
    let processedFiles = 0;

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = e => {
        const binaryStr = e.target?.result as string;
        let checksum;

        if (useFirst50kBytes) {
          const first50kBytes = binaryStr.slice(0, 50000);
          const fullSize = file.size;
          const prefixedContent = `${fullSize}_${first50kBytes}`;
          checksum = SparkMD5.hashBinary(prefixedContent);
        } else {
          checksum = SparkMD5.hashBinary(binaryStr);
        }

        if (!checksums.has(checksum)) {
          checksums.add(checksum);
          uniqueFilesWithHashes.push({ file, hash: checksum });
        }

        processedFiles += 1;
        if (processedFiles === files.length) {
          dispatch(upload({ files: uniqueFilesWithHashes, mainAssetId: assetId }));
        }
      };
      reader.readAsBinaryString(file);
    });
  };

  return (
    <div className={styles.relevantDocumentsContainer}>
      {relevantDocumentsLoading ? (
        <Loader />
      ) : (
        <>
          <div className={styles.documentsList}>
            {relevantDocuments &&
              relevantDocuments.length > 0 &&
              relevantDocuments.map((file, key) => <Item onDelete={() => handleDelete(file)} key={key} file={file} />)}
          </div>
          {uniqueSelectedFiles && uniqueSelectedFiles.length > 0 && (
            <div className={styles.documentsList}>
              <span className={styles.relationsTitle}> Relevant Documents selected from previous asset: </span>
              {uniqueSelectedFiles.map((file, key) => (
                <Item onDelete={() => handleDeleteForSelectedFile(file)} key={key} file={file} />
              ))}
            </div>
          )}
        </>
      )}

      <h6 className="mt-4"> Upload new relevant document </h6>

      <div className={styles.documentTypeSelector}>
        <span className={styles.title}> Document Type: </span>
        <Select
          value={selectedDocumentType}
          onChange={value => {
            dispatch(setSelectedDocumentType(value));
          }}
          options={items}
          className="py-1.5 px-3 text-sm-overwrite border-revert"
        />
      </div>

      <>
        {uploadInProgress && (
          <div className={styles.loaderOverlay}>
            <Loader absoluteCenter theme="dark" />
          </div>
        )}
        <UploadWrapper onUpload={handleUpload} allowedFormats={allowedFormats || []} />
      </>

      <div className={styles.documentsList}>
        {allFiles.map((file, key) => (
          <ItemUpload key={key} file={file} />
        ))}
      </div>
    </div>
  );
};

export default RelevantDocuments;
